import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Upload,
  Image as ImageIcon,
  Copy,
  CheckCircle,
  Loader2,
  Store, // ✅ NEW
} from "lucide-react";
import { useNavigate } from "react-router-dom"; // ✅ NEW

const IMGBB_API_KEY = "456110c1b6246eac6937a520866536c1";

export default function ImageUploader() {
  const navigate = useNavigate(); // ✅ NEW

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  /* ================= FILE SELECT ================= */
  const onSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;

    setFile(f);
    setPreview(URL.createObjectURL(f));
    setImageUrl("");
    setError("");
  };

  /* ================= UPLOAD ================= */
  const upload = async () => {
    if (!file) {
      setError("Please select an image first");
      return;
    }

    setUploading(true);
    setError("");

    const form = new FormData();
    form.append("image", file);

    try {
      const res = await fetch(
        `https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`,
        {
          method: "POST",
          body: form,
        }
      );

      const json = await res.json();

      if (!json.success) {
        throw new Error("Upload failed");
      }

      setImageUrl(json.data.url);
    } catch (err) {
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  /* ================= COPY ================= */
  const copy = async () => {
    await navigator.clipboard.writeText(imageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /* ================= UI ================= */
  return (
    <div className="min-h-screen px-6 py-20 bg-[#0b0b0b] text-white">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-xl mx-auto bg-[#0f1115] border border-white/10 rounded-3xl p-8 shadow-2xl"
      >
        {/* HEADER */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <ImageIcon /> Image to URL Converter
          </h2>

          {/* ✅ ADMIN SHOP BUTTON */}
          <button
            onClick={() => navigate("/admin/shop")}
            className="flex items-center gap-2 px-4 py-2 rounded-xl
                       bg-emerald-600 hover:bg-emerald-700
                       text-sm font-semibold"
          >
            <Store size={16} />
            Admin Shop
          </button>
        </div>

        {/* FILE INPUT */}
        <label className="block mb-3 font-semibold">
          Select Image
        </label>

        <input
          type="file"
          accept="image/*"
          onChange={onSelect}
          className="w-full text-sm text-gray-300
                     file:mr-4 file:py-2 file:px-4
                     file:rounded-lg file:border-0
                     file:bg-sky-600 file:text-white
                     hover:file:bg-sky-700"
        />

        {/* PREVIEW */}
        {preview && (
          <div className="mt-6 rounded-xl overflow-hidden border border-white/10">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-64 object-cover"
            />
          </div>
        )}

        {/* ERROR */}
        {error && (
          <div className="mt-4 bg-red-500/15 text-red-300 p-3 rounded-lg">
            {error}
          </div>
        )}

        {/* UPLOAD BUTTON */}
        <button
          onClick={upload}
          disabled={uploading}
          className="mt-6 w-full py-3 rounded-xl bg-sky-600 hover:bg-sky-700
                     font-semibold flex items-center justify-center gap-2"
        >
          {uploading ? (
            <>
              <Loader2 className="animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <Upload size={18} />
              Upload Image
            </>
          )}
        </button>

        {/* RESULT */}
        {imageUrl && (
          <div className="mt-6">
            <label className="text-sm font-semibold block mb-2">
              Image URL
            </label>

            <div className="flex gap-2">
              <input
                readOnly
                value={imageUrl}
                className="flex-1 px-3 py-2 rounded-lg bg-[#151820] border border-white/20 text-white"
              />

              <button
                onClick={copy}
                className="px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700"
              >
                {copied ? <CheckCircle /> : <Copy />}
              </button>
            </div>

            <p className="text-xs text-gray-400 mt-2">
              Paste this URL into Admin Cars or Admin Shop image field
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
